/**
 * Сегмент: выбор РОВНО ОДНОГО из нескольких.
 *
 * ⚠️ **Переключателей в продукте два вида и больше не бывает** (подход 2е
 * переноса дизайн-системы): одно из нескольких это сегмент, несколько из
 * нескольких это чип (`.chip`, разметка своя у каждого места). До 14.09.2026
 * видов было пять сразу: отдельные кнопки с `data-on` (режим недели в
 * расписании), чипы со счётчиками (состояние работ в домашке), этот сегмент со
 * скользящей подложкой, чипы-капсулы с `aria-pressed` (светлая или тёмная) и
 * ряд прямоугольных кнопок (путь в материалах). Выбор между двумя оставшимися
 * механический, вкуса в нём нет; пятый вид при этом переключателем не был
 * вовсе, это крошки, и кнопками они и остались.
 *
 * ⚠️ **Разметку сегмента рисует ТОЛЬКО этот файл**, и на это стоит замок
 * `lib/switch-kind.test.ts`. Заведён он был ровно от этой болезни: сначала два
 * одинаковых написания правил (`.cov__subjects` и `.picks__levels`, второе
 * появилось копированием), потом своя копия разметки в `LevelPicker` при живом
 * общем компоненте. Копии разъезжаются не видом, а клавиатурой: у одной стрелки
 * перепрыгивали выключенные сегменты, у другой упирались.
 *
 * ⚠️ **Скользящей подложки больше нет.** До 14.09.2026 активный сегмент был
 * залит акцентом, а ездила под ним `.seg__thumb`, ширину которой разметка
 * считала из `--seg-count`, а положение из `--seg-at`; подпись при этом
 * резервировала место под полужирное копией в `data-label`. Из подложки
 * следовало требование равной ширины сегментов, из него четыре сегмента по
 * «Просрочено» не влезали в телефон, а из этого домашка завела себе отдельный
 * вид переключателя. Теперь активный сегмент это поверхность карточки с
 * подъёмом, ширина ему не нужна, вес подписи не меняется.
 *
 * ⚠️ **Разметка это `radiogroup`, а не несколько кнопок с `aria-pressed`.**
 * Выбран ровно один сегмент, и объявлять читалке несколько независимых
 * тумблеров было бы неправдой.
 *
 * ⚠️ **Стрелки двигают и фокус, и выбор, и перепрыгивают выключенное.** Так
 * ведёт себя нативная группа радиокнопок, и человек, пришедший с клавиатуры,
 * ждёт именно этого. Упор на выключенном сегменте читался бы как «дальше
 * ничего нет», хотя дальше есть.
 */

/** Один сегмент. `id` уезжает в `data-seg`, `label` виден. */
export interface Segment {
  id: string;
  label: string;
  /**
   * Число рядом с подписью: «Все · 19». Отдельным узлом, а не строкой в
   * `label`, потому что оно набирается моноширинным: числа соседних сегментов
   * читают столбиком.
   *
   * ⚠️ `null` это «числа нет», и это НЕ то же самое, что ноль. Ноль от
   * отсутствия работ показывать надо, а ноль от неприехавших счётчиков врёт.
   */
  count?: number | null;
  /**
   * Выключен: выбрать нечего (у темы нет такого уровня) либо летит запрос.
   *
   * ⚠️ Выключенный сегмент рисуется, а не прячется: спрятанный читается как
   * «их всего два» и оставляет человека с вопросом, на который экран мог
   * ответить.
   */
  disabled?: boolean;
}

export function SegmentedRadio(props: {
  /** Имя группы для читалки. Обязательно: «предмет» и «сложность» это разные шкалы. */
  ariaLabel: string;
  segments: readonly Segment[];
  /** Что выбрано. `null` означает «пока ничего». */
  value: string | null;
  onPick: (id: string) => void;
}) {
  const at = props.segments.findIndex((s) => s.id === props.value);

  /*
    Выключение спрашивается дважды: разметкой у сегмента и стрелками, когда они
    ищут, куда шагнуть. Разъехавшись, эти два места дали бы стрелку, которая
    выбирает то, что мышью нажать нельзя, и ровно так и было у двух прежних
    копий переключателя.
  */
  const off = (segment: Segment): boolean => segment.disabled === true;

  const move = (event: React.KeyboardEvent<HTMLSpanElement>, step: -1 | 1): void => {
    /*
      От невыбранного шагаем с края, а не с минус первого: иначе стрелка вправо
      с пустым выбором прыгала бы на второй сегмент, перескочив первый.
    */
    const from = at < 0 ? (step === 1 ? -1 : props.segments.length) : at;
    let next: Segment | undefined;
    for (let k = from + step; k >= 0 && k < props.segments.length; k += step) {
      const candidate = props.segments[k] as Segment;
      if (!off(candidate)) {
        next = candidate;
        break;
      }
    }
    if (next === undefined) return;
    event.preventDefault();
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-seg="${next.id}"]`)?.focus();
    props.onPick(next.id);
  };

  /*
    ⚠️ Таб-порядок: ноль достаётся выбранному, а при пустом выборе ПЕРВОМУ
    включённому. Иначе у всех стоял бы минус первый, и в группу нельзя было бы
    попасть табом вовсе; на первом же выключенном фокус упёрся бы в сегмент,
    который нечем нажать.
  */
  const tabAt =
    at >= 0 ? at : props.segments.findIndex((segment) => !off(segment));

  return (
    <span
      className="seg"
      role="radiogroup"
      aria-label={props.ariaLabel}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") move(e, 1);
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") move(e, -1);
      }}
    >
      {props.segments.map((segment, i) => (
        <button
          key={segment.id}
          type="button"
          role="radio"
          className="seg__i"
          data-seg={segment.id}
          aria-checked={segment.id === props.value}
          disabled={off(segment)}
          tabIndex={i === tabAt ? 0 : -1}
          onClick={() => props.onPick(segment.id)}
        >
          {segment.label}
          {segment.count !== undefined && segment.count !== null && (
            <span className="seg__n">· {segment.count}</span>
          )}
        </button>
      ))}
    </span>
  );
}
